import React, { useState, useEffect } from 'react';
import '../styles/GoogleSheetsConfig.css';

const GoogleSheetsManager = ({ isOpen, onClose, onConfigUpdate, userRole }) => {
  const [config, setConfig] = useState({
    enabled: false,
    syncMode: 'hybrid',
    syncInterval: 300000,
    spreadsheetId: '',
    range: 'Leads!A2:H',
    credentialsConfigured: false
  });
  
  const [loading, setLoading] = useState({
    config: false,
    status: false,
    test: false,
    sync: false,
    save: false
  });
  
  const [message, setMessage] = useState({ type: '', text: '' });

  // Vérifier les permissions
  const hasAdminPermissions = userRole === 'administrateur' || userRole === 'admin';

  useEffect(() => {
    if (hasAdminPermissions && isOpen) {
      loadConfig();
    }
  }, [hasAdminPermissions, isOpen]);

  const loadConfig = async () => {
    try {
      setLoading(prev => ({ ...prev, config: true }));
      
  const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
  const response = await fetch(`${API_BASE}/google-sheets/config`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setConfig(data.data);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors du chargement');
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la configuration:', error);
      setMessage({ type: 'error', text: `Erreur lors du chargement: ${error.message}` });
    } finally {
      setLoading(prev => ({ ...prev, config: false }));
    }
  };

  const saveConfig = async () => {
    try {
      setLoading(prev => ({ ...prev, save: true }));
      
  const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
  const response = await fetch(`${API_BASE}/google-sheets/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setMessage({ type: 'success', text: 'Configuration sauvegardée avec succès' });
          if (onConfigUpdate) onConfigUpdate(config);
        } else {
          throw new Error(data.error || 'Erreur lors de la sauvegarde');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setMessage({ type: 'error', text: `Erreur lors de la sauvegarde: ${error.message}` });
    } finally {
      setLoading(prev => ({ ...prev, save: false }));
    }
  };

  const testConnection = async () => {
    try {
      setLoading(prev => ({ ...prev, test: true }));
      
  const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
  const response = await fetch(`${API_BASE}/google-sheets/test`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setMessage({ type: 'success', text: 'Connexion Google Sheets réussie !' });
      } else {
        setMessage({ type: 'error', text: `Erreur de connexion: ${result.error}` });
      }
    } catch (error) {
      console.error('Erreur lors du test de connexion:', error);
      setMessage({ type: 'error', text: `Erreur lors du test de connexion: ${error.message}` });
    } finally {
      setLoading(prev => ({ ...prev, test: false }));
    }
  };

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (!isOpen) {
    return null;
  }

  if (!hasAdminPermissions) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="config-header">
            <h2>Configuration Google Sheets</h2>
            <button onClick={onClose} className="close-btn">×</button>
          </div>
          <div className="access-denied">
            <p>🔒 Accès refusé - Permissions administrateur requises</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-large">
        <div className="config-header">
          <h2>🔗 Configuration Google Sheets</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
            <button onClick={() => setMessage({ type: '', text: '' })}>×</button>
          </div>
        )}

        {/* Guide de configuration */}
        <div className="config-guide" style={{
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '20px'
        }}>
          <h3 style={{ color: '#495057', marginBottom: '10px' }}>📋 Guide de configuration</h3>
          <ol style={{ paddingLeft: '20px', marginBottom: '0' }}>
            <li><strong>Préparez votre Google Sheet :</strong>
              <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                <li>Colonnes requises : A=Nom, B=Prénom, C=Email, D=Téléphone, E=Source, F=Statut, G=Notes, H=Date</li>
                <li>La première ligne doit contenir les en-têtes</li>
              </ul>
            </li>
            <li><strong>Obtenez l'ID de votre spreadsheet :</strong>
              <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                <li>Ouvrez votre Google Sheet</li>
                <li>Copiez l'ID depuis l'URL (partie entre /d/ et /edit)</li>
              </ul>
            </li>
            <li><strong>Configuration backend requise :</strong>
              <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                <li>Fichier credentials Google dans backend/config/</li>
                <li>Variables d'environnement dans .env</li>
              </ul>
            </li>
          </ol>
        </div>

        <div className="config-section">
          <h3>Configuration de base</h3>
          
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => handleConfigChange('enabled', e.target.checked)}
              />
              Activer Google Sheets comme source de données
            </label>
          </div>

          <div className="form-group">
            <label>ID du Spreadsheet Google:</label>
            <input
              type="text"
              value={config.spreadsheetId}
              onChange={(e) => handleConfigChange('spreadsheetId', e.target.value)}
              placeholder="1ABC-def_GHIjklmnop2QRS3TUVwxyz"
              disabled={!config.enabled}
            />
            <small>Trouvez l'ID dans l'URL de votre Google Sheet</small>
          </div>

          <div className="form-group">
            <label>Plage de données:</label>
            <input
              type="text"
              value={config.range}
              onChange={(e) => handleConfigChange('range', e.target.value)}
              placeholder="Leads!A2:H"
              disabled={!config.enabled}
            />
            <small>Ex: Leads!A2:H pour lire de A2 jusqu'à la colonne H</small>
          </div>

          <div className="form-group">
            <label>Mode de synchronisation:</label>
            <select
              value={config.syncMode}
              onChange={(e) => handleConfigChange('syncMode', e.target.value)}
              disabled={!config.enabled}
            >
              <option value="hybrid">Hybride (BDD + Google Sheets)</option>
              <option value="sheets-only">Google Sheets uniquement</option>
              <option value="database-fallback">BDD avec fallback Google Sheets</option>
            </select>
          </div>

          <div className="form-group">
            <label>Intervalle de synchronisation:</label>
            <select
              value={config.syncInterval}
              onChange={(e) => handleConfigChange('syncInterval', parseInt(e.target.value))}
              disabled={!config.enabled}
            >
              <option value={0}>Désactivé</option>
              <option value={300000}>5 minutes</option>
              <option value={600000}>10 minutes</option>
              <option value={1800000}>30 minutes</option>
              <option value={3600000}>1 heure</option>
            </select>
          </div>
        </div>

        <div className="config-actions">
          <button
            onClick={testConnection}
            disabled={loading.test || !config.enabled}
            className="btn btn-secondary"
          >
            {loading.test ? 'Test en cours...' : '🔍 Tester la connexion'}
          </button>
          
          <button
            onClick={saveConfig}
            disabled={loading.save}
            className="btn btn-primary"
          >
            {loading.save ? 'Sauvegarde...' : '💾 Sauvegarder'}
          </button>
          
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default GoogleSheetsManager;
